import React from 'react';

import shipSvg from '@assets/img/icons/ship.svg';
import truckSvg from '@assets/img/icons/truck.svg';
import warehouseSvg from '@assets/img/icons/warehouse.svg';
import { Button } from '@components/Button';
import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { PrintCargoTrackingDetails } from '@components/utilities/CargoTracking/helpers';
import { TrackingDetailItemNote } from '@components/utilities/CargoTracking/TrackingDetailItemNote';
import { trackingDataMeta } from '@data/cargoTracking';
import Collapse from '@material-ui/core/Collapse';
import Grid from '@material-ui/core/Grid';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import {
  DomesticTrackingDetailItem,
  TrackingData,
  TrackingDataHelper,
  TrackingDetailItem,
  TransportStatus,
} from '@models/TrackingData';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { recordToKV, stringifySearch, toCST, toJST, toJSTDate } from '@utils/types';
import clsx from 'clsx';
import { filter, flatMap, map, pipe, sortBy } from 'remeda';

const strings = createStrings({
  ja: {
    nThItem: (n: number) => `${n}件目`,
    d2dNo: 'D2D番号',
    date: '日付',
    transportStatus: '輸送状況',
    details: '詳細',
    printDetails: '詳細印刷',
    dateTime: '日時',
    note: '備考',
    notFound: '無効な番号です。ご確認ください。',
    domesticDetails: '国内配達状況詳細',
    copiedAlert: '追跡番号はコピーされました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  trackingHeaderContainer: {
    ...common.bigText,
    ...common.textGray800,
    ...common.fontWeight500,
    '&$printable': {
      ...common.middleText,
      ...common.fontWeight500,
      backgroundColor: theme.palette.grey[200],
      '-webkit-print-color-adjust': 'exact',
    },
  },
  trackingItemContainer: {
    ...common.bigText,
    ...common.fontWeight500,
    ...common.textGray700,
    '&$printable': {
      ...common.middleText,
    },
  },
  trackingItemDetails: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.light,
    },
    '& a': {
      textDecoration: 'underline',
    },
  },
  expandMore: {
    fontSize: 30,
    margin: '-5px 0 -5px 5px',
    position: 'relative',
    top: 2.5,
  },
  trackingDetailsContainer: {
    ...common.flexColumn,
    backgroundColor: theme.palette.grey[50],
    padding: 20,
    marginBottom: 20,
    [theme.breakpoints.down('sm')]: {
      padding: '1rem',
    },
  },
  trackingDetailsHeader: {
    ...common.flexInline,
    alignItems: 'center',
  },
  trackingTable: {
    ...common.bigText,
    borderSpacing: 0,
    marginTop: 20,
    '&$printable': {
      ...common.middleText,
    },
    '&$printable th, &$printable td': {
      padding: 12,
    },
    '&$printable a': {
      ...common.textGray700,
      textDecoration: 'initial',
    },
    '& th': {
      ...common.fontWeight600,
      ...common.textGray500,
      backgroundColor: theme.palette.grey['200'],
      textAlign: 'left',
      padding: 20,
      '-webkit-print-color-adjust': 'exact',
    },
    '& td': {
      ...common.textGray700,
      textAlign: 'left',
      padding: 20,
      borderTop: `1px solid ${theme.palette.grey['200']}`,
    },
    '&:hover': {
      color: theme.palette.primary.light,
    },
    '& a': {
      cursor: 'pointer',
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  default: {},
  printable: {},
  dialogTitle: {
    ...common.biggerText,
    ...common.fontWeight500,
    ...common.textGray800,
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 60,
  },
  dialogTable: {
    ...common.bigText,
    ...common.textGray700,
    '& a:hover': {
      color: theme.palette.primary.light,
    },
    '& a': {
      cursor: 'pointer',
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    '& td': {
      padding: '10px 10px',
    },
  },
  errorRow: {
    color: theme.palette.error.main,
  },
  tableHeadCell: {
    color: 'inherit',
    fontSize: 'inherit',
  },
  tableBodyCell: {
    borderBottom: 'none',
    color: 'inherit',
    fontSize: 'inherit',
  },
  trackingProgressContainer: {
    display: 'grid',
    gridTemplateColumns: '70px 1fr 70px 1fr 70px',
    rowGap: 16,
    paddingLeft: 20,
    paddingRight: 100,
  },
  trackingProgressDotContainer: {
    ...common.flexCenter,
  },
  trackingProgressDot: {
    backgroundColor: theme.palette.primary.main,
    opacity: 0.5,
    height: 8,
    width: 8,
    borderRadius: '50%',
  },
  trackingProgressLineContainer: {
    ...common.flexColumn,
    ...common.justifyCenter,
    margin: '0 -20px',
  },
  trackingProgressLine: {
    borderTop: `1px dashed ${theme.palette.primary.main}`,
  },
}));

export interface TrackingHeaderProps {
  variant?: 'printable' | 'default';
}

export const TrackingHeader: React.FC<TrackingHeaderProps> = (props) => {
  const { variant = 'default' } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const headClass = clsx(classes.trackingHeaderContainer, classes[variant]);

  return (
    <TableHead classes={{ root: headClass }}>
      <TableRow>
        <TableCell classes={{ root: classes.tableHeadCell }} />
        <TableCell classes={{ root: classes.tableHeadCell }}>{strings.d2dNo}</TableCell>
        <TableCell classes={{ root: classes.tableHeadCell }}>{strings.date}</TableCell>
        <TableCell classes={{ root: classes.tableHeadCell }}>{strings.transportStatus}</TableCell>
      </TableRow>
    </TableHead>
  );
};

export interface TrackingItemProps {
  index: number;
  expanded?: boolean;
  onExpand?: () => void;
  trackNo: string;
  trackingData?: TrackingData;
  variant?: 'printable' | 'default';
}

export const TrackingItem: React.FC<TrackingItemProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { index, expanded = false, onExpand, trackNo, trackingData, variant = 'default' } = props;

  const rowClassNames = clsx(classes.trackingItemContainer, classes[variant]);

  if (trackingData) {
    const [transportStatus, trackingDetailsItem] =
      new TrackingDataHelper(trackingData).getLastVisibleInCargoTrackingTransportStatus() ?? [];

    return (
      <>
        <TableRow classes={{ root: rowClassNames }}>
          <TableCell classes={{ root: classes.tableBodyCell }}>
            <span style={{ whiteSpace: 'nowrap' }}>{strings.nThItem(index + 1)}</span>
          </TableCell>
          <TableCell classes={{ root: classes.tableBodyCell }}>{trackingData.track_no}</TableCell>
          <TableCell classes={{ root: classes.tableBodyCell }}>
            {trackingDetailsItem ? toJSTDate(new Date(trackingDetailsItem.time)) : '-'}
          </TableCell>
          <TableCell classes={{ root: classes.tableBodyCell }}>
            <span>{transportStatus ?? '-'}</span>
            {variant === 'default' && (
              <div
                style={{ float: 'right' }}
                className={classes.trackingItemDetails}
                onClick={onExpand}
              >
                <a>{strings.details}</a>
                <ExpandMoreButton
                  expanded={expanded}
                  classes={{
                    root: classes.expandMore,
                  }}
                />
              </div>
            )}
          </TableCell>
        </TableRow>

        <TableRow classes={{ root: rowClassNames }}>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            classes={{ root: classes.tableBodyCell }}
            colSpan={4}
          >
            <Collapse in={expanded}>
              <TrackingDetails trackingData={trackingData} />
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  return (
    <TableRow classes={{ root: clsx(rowClassNames, classes.errorRow) }}>
      <TableCell classes={{ root: classes.tableBodyCell }}>{strings.nThItem(index + 1)}</TableCell>
      <TableCell classes={{ root: classes.tableBodyCell }}>{trackNo}</TableCell>
      <TableCell classes={{ root: classes.tableBodyCell }} />
      <TableCell classes={{ root: classes.tableBodyCell }}>{strings.notFound}</TableCell>
    </TableRow>
  );
};

export interface TrackingDetailsProps {
  trackingData: TrackingData;
}

export const TrackingDetails: React.FC<TrackingDetailsProps> = (props) => {
  const { trackingData } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const detailsQuery = stringifySearch<PrintCargoTrackingDetails>({
    trackNo: trackingData.track_no,
  });

  const [transportStatus, trackingDetailsItem] =
    new TrackingDataHelper(trackingData).getLastVisibleInCargoTrackingTransportStatus() ?? [];

  return (
    <div className={classes.trackingDetailsContainer}>
      <div className={classes.trackingDetailsHeader}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <TrackingProgress />
          </Grid>
          <Grid container alignItems="center" item xs={12} lg={5} spacing={4}>
            <Grid item xs={7}>
              <div style={{ marginLeft: '1rem' }}>
                <div
                  className={clsx(classes.fontWeight500, classes.textGray800, classes.biggerText)}
                >
                  {transportStatus ?? '-'}
                </div>
                <div className={clsx(classes.textGray700, classes.normalText)}>
                  {trackingDetailsItem
                    ? (trackingDataMeta[transportStatus!].id === 'Asia/Tokyo' ? toJST : toCST)(
                        new Date(trackingDetailsItem.time),
                      )
                    : '-'}
                </div>
              </div>
            </Grid>
            <Grid container justify="flex-end" item xs={5}>
              <a
                href={`${url(
                  '/utilities/CargoTracking/PrintCargoTrackingDetails',
                )}?${detailsQuery}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  color="primary"
                  text={strings.printDetails}
                  className={classes.biggerTextImportant}
                />
              </a>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <TrackingTable trackingData={trackingData} />
    </div>
  );
};

export const TrackingProgress: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderItem = (image: string, imgStyle: React.CSSProperties) => {
    return (
      <div className={classes.flexCenter}>
        <img src={image} alt="Step" style={imgStyle} />
      </div>
    );
  };

  const renderDot = () => {
    return (
      <div className={classes.trackingProgressDotContainer}>
        <div className={classes.trackingProgressDot} />
      </div>
    );
  };

  const renderLine = () => {
    return (
      <div className={classes.trackingProgressLineContainer}>
        <div className={classes.trackingProgressLine} />
      </div>
    );
  };

  return (
    <div className={classes.trackingProgressContainer}>
      {renderItem(warehouseSvg, {
        width: 46,
        height: 49,
      })}
      <div />
      {renderItem(shipSvg, {
        width: 65,
        height: 45,
        position: 'relative',
        top: 2,
      })}
      <div />
      {renderItem(truckSvg, {
        width: 65,
        height: 38,
        position: 'relative',
        top: 2,
      })}
      {renderDot()}
      {renderLine()}
      {renderDot()}
      {renderLine()}
      {renderDot()}
    </div>
  );
};

export interface TrackingTableProps {
  trackingData: TrackingData;
  variant?: 'printable' | 'default';
}

export const TrackingTable: React.FC<TrackingTableProps> = (props) => {
  const { trackingData, variant = 'default' } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderTrackingDetailItem = (
    statusName: TransportStatus,
    item: TrackingDetailItem | DomesticTrackingDetailItem,
    i: number,
  ) => {
    const timeFormatter = trackingDataMeta[statusName].name === 'CST' ? toCST : toJST;

    return (
      <tr key={i}>
        <td>{statusName}</td>
        <td>{timeFormatter(new Date(item.time))}</td>
        <td>
          <TrackingDetailItemNote item={item} statusName={statusName} />
        </td>
      </tr>
    );
  };

  return (
    <>
      <table className={clsx(classes.trackingTable, classes[variant])}>
        <tbody>
          <tr>
            <th>{strings.transportStatus}</th>
            <th>{strings.dateTime}</th>
            <th>{strings.note}</th>
          </tr>
          {pipe(
            recordToKV(trackingData.data),
            filter(([k, _]) =>
              TrackingDataHelper.getVisibleInCargoTrackingTransportStatus().includes(k as any),
            ),
            flatMap(([k, v]) => (v ? v.map((v) => [k, v] as const) : [])),
            sortBy([([_, v]) => new Date(v.time).getTime(), 'desc']),
            map.indexed(([k, v], i) => renderTrackingDetailItem(k, v, i)),
          )}
        </tbody>
      </table>
    </>
  );
};
